import { createSelector } from 'reselect'
import { getCruiseItems, hasLuxPlusInclusionsInCruiseBooking, getCurrentCheckoutStepId as getCruiseCheckoutStepId } from './cruiseSelectors'
import { getTourCheckoutSteps } from 'components/App/Header/HeaderCheckout/StepsHeaderCheckout/steps/tours'
import { CRUISE_CHECKOUT_HEADER_STEPS } from 'components/App/Header/HeaderCheckout/StepsHeaderCheckout/steps/cruises'
import { getCheckoutRouteFromURL } from 'checkout/lib/utils/misc/steps'
import { EmptyArray } from 'lib/array/arrayUtils'
import { CheckoutPageId } from 'checkout/constants/pages'
import { getCanRedeemLuxPlusBenefits, getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { FLIGHTS_CHECKOUT_HEADER_STEPS, FlightChangeDatesPageStep } from 'components/App/Header/HeaderCheckout/StepsHeaderCheckout/steps/flights'
import { findPostPurchaseCheckout } from 'lib/checkout/checkoutUtils'
import { getFlightChangeDatesPageLocation } from 'checkout/lib/utils/flights/dateChangeUtils'
import { hasCruiseHotelRecommendationsAvailable } from 'checkout/selectors/cruiseSelectors'
import { hasInsuranceItem } from './navigationSelectors'

export const selectCheckoutSteps = createSelector(
  (state: App.State) => getTourCheckoutSteps(state),
  (state: App.State) => getCruiseItems(state),
  (state: App.State) => getIsLuxPlusEnabled(state),
  (state: App.State) => getCanRedeemLuxPlusBenefits(state),
  (state: App.State) => hasLuxPlusInclusionsInCruiseBooking(state),
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => hasCruiseHotelRecommendationsAvailable(state),
  (state: App.State) => hasInsuranceItem(state),
  (
    tourCheckoutSteps,
    cruiseItems,
    luxPlusEnabled,
    checkCanRedeemLuxPlusBenefits,
    hasLuxPlusInclusionsInCruiseBooking,
    postPurchaseMode,
    hasCruiseHotelRecommendationsAvailable,
    orderHasInsurance,
  ): Array<App.Checkout.CheckoutStep> => {
    const isCruise = cruiseItems.length > 0
    let allCruiseSteps = CRUISE_CHECKOUT_HEADER_STEPS

    if (postPurchaseMode === 'flight-change-dates') {
      // Filter out insurance step if order doesn't have insurance
      if (!orderHasInsurance) {
        return FLIGHTS_CHECKOUT_HEADER_STEPS.filter(
          step => step.key !== FlightChangeDatesPageStep.INSURANCE_SELECT_DATES,
        )
      }
      return FLIGHTS_CHECKOUT_HEADER_STEPS
    }

    if (isCruise) {
      // If LuxPlus is not enabled, remove it from the steps
      if (!luxPlusEnabled || !hasLuxPlusInclusionsInCruiseBooking || checkCanRedeemLuxPlusBenefits) {
        allCruiseSteps = allCruiseSteps.filter(step => step.key !== CheckoutPageId.LuxPlus)
      }

      // If hotel recommendations A/B test is not enabled, remove it from the steps
      if (!hasCruiseHotelRecommendationsAvailable) {
        allCruiseSteps = allCruiseSteps.filter(step => step.key !== CheckoutPageId.CruiseHotelRecommendation)
      }

      return allCruiseSteps
    }

    if (tourCheckoutSteps.length > 1) {
      return tourCheckoutSteps
    }

    return EmptyArray
  },
)

export const selectCheckoutCurrentStep = createSelector(
  (state: App.State) => state.router.location.pathname,
  (state: App.State) => getCruiseCheckoutStepId(state),
  (state: App.State) => getTourCheckoutSteps(state),
  (state: App.State) => getCruiseItems(state),
  (state: App.State) => selectCheckoutSteps(state),
  (state: App.State) => state.flights.flightsDateChange.currentStep,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (
    pathname,
    cruiseCheckoutStepId,
    tourCheckoutSteps,
    cruiseItems,
    steps,
    flightsDateChangeStep,
    postPurchaseMode,
  ): App.Checkout.CheckoutStep | undefined => {
    const isCruise = cruiseItems.length > 0

    let stepKey: string | undefined
    if (isCruise) {
      stepKey = cruiseCheckoutStepId
    }

    if (tourCheckoutSteps.length > 1) {
      stepKey = getCheckoutRouteFromURL(pathname)
    }

    if (postPurchaseMode === 'flight-change-dates') {
      // Use URL detection to determine current step for flight change dates flow
      // This handles cases where Redux state may not match the actual page (e.g., on purchase page)
      const { isOnPurchasePage, isOnInsurancePage } = getFlightChangeDatesPageLocation(pathname)
      if (isOnPurchasePage) {
        return FLIGHTS_CHECKOUT_HEADER_STEPS.find(step => step.key === FlightChangeDatesPageStep.REVIEW_AND_PAY)
      }
      if (isOnInsurancePage) {
        return FLIGHTS_CHECKOUT_HEADER_STEPS.find(step => step.key === FlightChangeDatesPageStep.INSURANCE_SELECT_DATES)
      }
      // For internal steps within flight-change-select-flight-to-change page, use Redux state
      return FLIGHTS_CHECKOUT_HEADER_STEPS.find(step => step.key === flightsDateChangeStep)
    }

    return steps.find(step => {
      return step.key === stepKey ||
        // if any sub-step matches the current key, we pick the parent as the current active step.
        step.subSteps?.some(subStep => subStep.key === stepKey)
    })
  },
)
